import { useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { validateUpload } from "../lib/uploadLimits";
import AuthImage from "./AuthImage";
import { Camera, Loader2 } from "lucide-react";

/**
 * Uploads a single file to /api/uploads/{kind} and returns the file_id via
 * onUploaded(file_id, url). Renders a clickable surface that overlays the
 * current image (if any).
 */
export default function MediaUploader({
  kind,
  currentUrl,
  onUploaded,
  className = "",
  shape = "rect",
  accept = "image/jpeg,image/png,image/webp",
  label = "Change photo",
  testId,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handle = async (file) => {
    if (!file) return;
    setError("");
    const sizeErr = validateUpload(kind, file);
    if (sizeErr) { setError(sizeErr); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded?.(data.file_id, data.url);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const isRound = shape === "round";
  return (
    <div className={`relative group ${className}`} data-testid={testId}>
      <AuthImage
        src={currentUrl}
        alt={kind}
        className={`w-full h-full object-cover ${isRound ? "rounded-full" : "rounded-md"}`}
        fallback={
          <div
            className={`w-full h-full bg-[#0d1b2e] border border-dashed border-[rgba(0,170,255,0.18)] flex items-center justify-center ${isRound ? "rounded-full" : "rounded-md"}`}
          >
            <Camera size={20} className="text-[#94a3b8]" />
          </div>
        }
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`absolute inset-0 flex items-center justify-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-white bg-black/0 group-hover:bg-black/55 transition-all duration-200 ${isRound ? "rounded-full" : "rounded-md"} ${uploading ? "bg-black/65" : ""}`}
        data-testid={`${testId || kind}-upload-trigger`}
        disabled={uploading}
      >
        {uploading ? <Loader2 className="animate-spin" size={16} /> : <Camera size={14} />}
        <span className="opacity-0 group-hover:opacity-100 transition-opacity">
          {uploading ? "Uploading…" : label}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
      />
      {error && (
        <div className="absolute -bottom-6 left-0 right-0 text-[11px] text-red-400 font-mono-tech">
          {error}
        </div>
      )}
    </div>
  );
}
